import { v4 as uuidV4 } from 'uuid';
import {
  ConditionQuantifier,
  HubConditionOperator,
  SubCondition,
} from 'dorian-shared/types/branch/HubOption';
import type { BorderCondition } from 'dorian-shared/types/branch/HubOption';
import { registerModsSerializer } from './hubSerialize';
import { isValidBorderHex } from './borderConditionValidation';
import { SYSTEM_DEFAULT_BORDER_COLOR } from './HubOptions/Border/BorderColorInput';

type ModRule = {
  variableId: number | null;
  operator: string;
  value: string | { type: 'variable'; variableId: number };
};

type Mod = {
  prop: string;
  sortOrder: number;
  val: Record<string, unknown>;
  when: { match: string; rules: ModRule[] };
};

const BORDER_PROP = 'borderColor';

function toRuleValue(sub: { intValue?: string; varValue?: number }): ModRule['value'] {
  if (sub.varValue && sub.varValue > 0) {
    return { type: 'variable', variableId: sub.varValue };
  }
  return sub.intValue ?? '';
}

function fromRuleValue(value: ModRule['value']): Pick<SubCondition, 'intValue' | 'varValue'> {
  if (typeof value === 'object' && value.type === 'variable') {
    return { varValue: value.variableId };
  }
  return { intValue: String(value) };
}

/**
 * Deserialize mods with prop: "borderColor" into BorderCondition[] for frontend UI.
 * 1:1 mapping — one mod = one condition.
 */
function isModLike(m: unknown): m is Mod {
  return m != null && typeof m === 'object' && 'prop' in m;
}

export function deserializeBorderConditions(mods: unknown[]): BorderCondition[] {
  const source = Array.isArray(mods) ? mods : [];
  const borderMods = source
    .filter(isModLike)
    .filter((m) => m.prop === BORDER_PROP)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return borderMods.map((mod): BorderCondition => {
    const rawColor = (mod.val && typeof mod.val === 'object') ? (mod.val as { color?: unknown }).color : undefined;
    const color = typeof rawColor === 'string' && isValidBorderHex(rawColor) ? rawColor : SYSTEM_DEFAULT_BORDER_COLOR;
    const when = (mod.when && typeof mod.when === 'object') ? mod.when : { match: 'all', rules: [] };
    const rules = Array.isArray(when.rules) ? when.rules : [];
    return {
      uuid: uuidV4(),
      color,
      quantifier: when.match === 'any' ? ConditionQuantifier.Any : ConditionQuantifier.All,
      subConditions: rules.map((rule): SubCondition => ({
        uuid: uuidV4(),
        variableId: rule?.variableId ?? null,
        operator: (rule?.operator ?? '') as HubConditionOperator,
        ...fromRuleValue(rule?.value ?? ''),
      })),
    };
  });
}

const serializeBorderMods = (option: { borderConditions?: BorderCondition[] }) => {
  const borderConditions = option.borderConditions ?? [];
  return {
    mods: borderConditions.map((cond, i) => ({
      prop: BORDER_PROP,
      sortOrder: i,
      val: { color: cond.color },
      when: {
        match: cond.quantifier,
        rules: cond.subConditions.map((sub) => ({
          variableId: sub.variableId,
          operator: sub.operator,
          value: toRuleValue(sub),
        })),
      },
    })),
    handledProps: [BORDER_PROP],
  };
};

registerModsSerializer(serializeBorderMods as Parameters<typeof registerModsSerializer>[0]);
